import { useBlocker } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useFormDirtyStore } from '../../stores/form-dirty-store';
import { LeaveAlertDialog } from './LeaveAlertDialog';
import type { LeaveAlertOption } from './types';

type LeavePageAlertProps = {
  /** アラートダイアログのオプション */
  options?: LeaveAlertOption | undefined;
};

/**
 * フォーム編集中のページ離脱を防ぐアラートコンポーネント
 *
 * TanStack Router のナビゲーションをブロックし、
 * ユーザーに確認ダイアログを表示します。
 *
 * @example
 * ```tsx
 * // Form.tsx で自動的に組み込まれるため、直接使用する必要はありません
 * <FormWithAlert leavePageAlertCondition={{ isDirty, isSubmitting, isSubmitSuccessful }}>
 *   ...
 * </FormWithAlert>
 * ```
 */
export const LeavePageAlert = ({ options }: LeavePageAlertProps) => {
  const { isDirty, setIsDirty } = useFormDirtyStore();
  const [isOpen, setIsOpen] = useState(false);

  const blocker = useBlocker({
    // Zustandストアから直接読み取ることで、ログアウト等でsetIsDirty(false)した直後の
    // navigate呼び出しでもブロックされないようにする
    shouldBlockFn: () => useFormDirtyStore.getState().isDirty,
    enableBeforeUnload: isDirty,
    disabled: !isDirty,
    withResolver: true,
  });

  useEffect(() => {
    if (blocker.status === 'blocked') {
      setIsOpen(true);
    } else if (blocker.status === 'idle') {
      setIsOpen(false);
    }
  }, [blocker.status]);

  const handleConfirm = () => {
    setIsOpen(false);
    setIsDirty(false);
    if (blocker.proceed) {
      blocker.proceed();
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    if (blocker.reset) {
      blocker.reset();
    }
  };

  return (
    <LeaveAlertDialog
      isOpen={isOpen}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      options={options}
    />
  );
};
